/**
 * Track 4.18 — Group SOC view at /app/cdm/group-soc.
 *
 * For users holding `scope_kind='global_view'` (or legacy admin role):
 *   - Per-entity rollup grid: assets / conflicts / critical risks /
 *     compliance % + top-3 sector actors.
 *   - Aggregate stats: total assets, top-5 vendors, total open
 *     compliance findings.
 *   - Click an entity row → "View as this entity" sets the
 *     X-CDM-Org-Id header (via the tenant switcher's localStorage key)
 *     and navigates to /app/cdm. Every drill-down lights up a
 *     cdm.cross_tenant_read audit row on the next page load.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Shield, AlertTriangle, ChevronRight, Globe, BarChart3, ListChecks } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface EntityRow {
  org_id: string
  name: string
  kind: string
  jurisdiction: string | null
  regulatory_zone: string | null
  data_residency: string | null
  isolation_mode: string | null
  sector: string | null
  asset_count: number
  user_count: number
  conflicts_open: number
  critical_risks: number
  compliance_open: number
  compliance_pct: number | null
  last_pull_at: string | null
  top_actors: { name?: string; actor_id?: string; score?: number }[]
}

interface Vendor {
  vendor: string
  asset_count: number
  user_count: number
  install_count: number
  cve_open_count: number
}

interface GroupSocResponse {
  count: number
  entities: EntityRow[]
  aggregate: {
    total_assets: number
    total_conflicts_open: number
    total_compliance_open: number
  }
  top_vendors: Vendor[]
}

const KIND_TONE: Record<string, string> = {
  group:          'bg-blue/10 text-blue-light border-blue/30',
  entity:         'bg-emerald/10 text-emerald border-emerald/30',
  business_unit:  'bg-cyan/10 text-cyan border-cyan/30',
  ring_fenced:    'bg-rose/10 text-rose-light border-rose/30',
}

export default function CdmGroupSocPage() {
  const [data, setData] = useState<GroupSocResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get<GroupSocResponse>('/api/cdm/group-soc/summary')
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load group-SOC summary'))
      .finally(() => setLoading(false))
  }, [])

  const viewAs = (orgId: string) => {
    try {
      localStorage.setItem('cdm-org-id', orgId)
    } catch {}
    navigate('/app/cdm')
    if (typeof window !== 'undefined') {
      // Force reload so the next /api/cdm/* fetch picks up the new
      // X-CDM-Org-Id header and every page-level cache invalidates.
      setTimeout(() => window.location.reload(), 0)
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-3 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Globe size={18} className="text-blue-light" /> GROUP SOC
        </h1>
        <span className="text-[10px] text-text-muted">
          Cross-tenant rollup · global_view scope
        </span>
      </div>

      {loading && <div className="card p-6 text-text-muted text-sm">Loading…</div>}
      {err && (
        <div className="card p-4 border border-rose/30 bg-rose/5 text-rose-light text-sm">
          {err}
        </div>
      )}

      {data && (
        <>
          {/* Aggregate tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Tenants"          value={data.count} icon={<Building2 size={14} />} />
            <Tile label="Total assets"     value={data.aggregate.total_assets} icon={<BarChart3 size={14} />} />
            <Tile label="Open conflicts"   value={data.aggregate.total_conflicts_open}
                  icon={<AlertTriangle size={14} />}
                  tone={data.aggregate.total_conflicts_open > 0 ? 'amber' : undefined} />
            <Tile label="Open compliance"  value={data.aggregate.total_compliance_open}
                  icon={<ListChecks size={14} />}
                  tone={data.aggregate.total_compliance_open > 0 ? 'rose' : undefined} />
          </div>

          {/* Entity grid */}
          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
              <Building2 size={14} /> Per-entity rollup
            </h2>
            {data.entities.length === 0 && (
              <p className="text-xs text-text-muted">No entity-kind orgs in the tree yet.</p>
            )}
            {data.entities.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-surface-raised">
                    <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                      <th className="text-left px-2 py-1.5">Entity</th>
                      <th className="text-left px-2 py-1.5">Kind</th>
                      <th className="text-left px-2 py-1.5">Region</th>
                      <th className="text-right px-2 py-1.5">Assets</th>
                      <th className="text-right px-2 py-1.5">Users</th>
                      <th className="text-right px-2 py-1.5">Conflicts</th>
                      <th className="text-right px-2 py-1.5">Critical risks</th>
                      <th className="text-right px-2 py-1.5">Compliance</th>
                      <th className="text-left px-2 py-1.5">Sector actors</th>
                      <th className="text-right px-2 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.entities.map(e => (
                      <tr key={e.org_id} className="hover:bg-surface-hover">
                        <td className="px-2 py-2 font-medium text-text-primary">
                          {e.name}
                          {e.isolation_mode && e.isolation_mode !== 'shared' && (
                            <span className="ml-1.5 text-[9px] text-rose-light uppercase">{e.isolation_mode}</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <span className={clsx(
                            'badge text-[9px] uppercase tracking-wider border px-1 py-0.5',
                            KIND_TONE[e.kind] || 'bg-surface-raised text-text-muted border-border',
                          )}>{e.kind.replace('_', ' ')}</span>
                        </td>
                        <td className="px-2 py-2 text-[10px] text-text-muted font-mono">
                          {[e.jurisdiction, e.regulatory_zone, e.data_residency].filter(Boolean).join(' · ')}
                        </td>
                        <td className="px-2 py-2 text-right font-mono">{e.asset_count.toLocaleString('en-GB')}</td>
                        <td className="px-2 py-2 text-right font-mono">{e.user_count.toLocaleString('en-GB')}</td>
                        <td className={clsx('px-2 py-2 text-right font-mono',
                                            e.conflicts_open > 0 && 'text-amber-light')}>
                          {e.conflicts_open}
                        </td>
                        <td className={clsx('px-2 py-2 text-right font-mono',
                                            e.critical_risks > 0 && 'text-rose-light')}>
                          {e.critical_risks}
                        </td>
                        <td className="px-2 py-2 text-right font-mono">
                          {e.compliance_pct !== null ? `${e.compliance_pct}%` : '—'}
                          {e.compliance_open > 0 && (
                            <span className="ml-1 text-[9px] text-rose-light">({e.compliance_open})</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-[10px] text-text-secondary">
                          {e.top_actors.length > 0
                            ? e.top_actors.slice(0, 3).map(a => a.name || a.actor_id).filter(Boolean).join(', ')
                            : <span className="text-text-muted">—</span>}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => viewAs(e.org_id)}
                            className="btn-secondary text-[10px] inline-flex items-center gap-1 px-2 py-1">
                            View as <ChevronRight size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Top vendors */}
          {data.top_vendors.length > 0 && (
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                <Shield size={14} /> Top-5 vendors by concentration (group)
              </h2>
              <table className="w-full text-xs">
                <thead className="bg-surface-raised">
                  <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                    <th className="text-left px-2 py-1.5">Vendor</th>
                    <th className="text-right px-2 py-1.5">Assets</th>
                    <th className="text-right px-2 py-1.5">Users</th>
                    <th className="text-right px-2 py-1.5">Installs</th>
                    <th className="text-right px-2 py-1.5">Open CVEs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.top_vendors.map(v => (
                    <tr key={v.vendor}>
                      <td className="px-2 py-1.5 font-medium text-text-primary">{v.vendor}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{v.asset_count.toLocaleString('en-GB')}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{v.user_count.toLocaleString('en-GB')}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{v.install_count.toLocaleString('en-GB')}</td>
                      <td className={clsx('px-2 py-1.5 text-right font-mono',
                                          v.cve_open_count > 0 && 'text-rose-light')}>
                        {v.cve_open_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Tile({
  label, value, icon, tone,
}: {
  label: string
  value: number
  icon: React.ReactNode
  tone?: 'amber' | 'rose'
}) {
  return (
    <div className={clsx(
      'card p-3',
      tone === 'amber' && 'border-amber/30',
      tone === 'rose'  && 'border-rose/30',
    )}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-text-muted">
        {icon} {label}
      </div>
      <div className={clsx(
        'text-2xl font-black font-mono mt-1',
        tone === 'amber' && 'text-amber-light',
        tone === 'rose'  && 'text-rose-light',
        !tone && 'text-text-primary',
      )}>{value.toLocaleString('en-GB')}</div>
    </div>
  )
}
